import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import ContactCreationBankSection from 'src/Components/Forms/Contact/PhysicalContactForm/BankSectionForm/BankSectionForm.component';
import { CategorySectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/CategorySectionForm';
import { CompanyNotesSectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/CompanyNotesSectionForm';
import { CompanySectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/CompanySectionForm';
import { ContactKindSectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/ContactKindSectionForm';
import { ContactSectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/ContactSectionForm';
import { IdentitySectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/IdentitySectionForm';
import { InterlocutorsSectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/InterlocutorsSectionForm';
import { PaymentModeSectionForm } from 'src/Components/Forms/Contact/PhysicalContactForm/PaymentModeSectionForm';
import { addressesApi } from 'src/Services/API';
import { CompanySearchResultDto, ContactKind } from 'src/Services/API/generated';

import styles from './PhysicalContactForm.module.scss';
import { Props } from './PhysicalContactForm.types';

const PhysicalContactForm = ({
  onSubmit,
  onCancel,
  form,
  submitButtonLabel,
  submitAndAddAnotherButtonLabel,
  initialAddressText,
  loading,
  isCreation,
  companyHeader,
  autoFocusCompanyName,
  afterIdentity,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [addressPrefill, setAddressPrefill] = useState<{ text: string; placeId?: string }>();

  // The register address becomes the Google place right away: a prefilled field that is not
  // saved would be a silent trap. No match: the text stays and the field asks to pick one.
  const prefillCompanyAddress = async (company: CompanySearchResultDto) => {
    form.setFieldValue('placeId', '');
    try {
      const { suggestions } = await queryClient.fetchQuery(
        ['get-address-autocomplete-suggestions', company.address],
        async () =>
          (await addressesApi.addressesControllerGetAutocompleteSuggestions(company.address)).data,
        { staleTime: 5 * 60 * 1000 }
      );
      const best = suggestions[0]?.placePrediction;
      if (best?.placeId && best.text.text) {
        setAddressPrefill({ text: best.text.text, placeId: best.placeId });
        return;
      }
    } catch {
      // Falls back on the text below
    }
    setAddressPrefill({ text: company.address });
  };

  const isCompany = form.values.kind === ContactKind.Company;
  // The usual payment mode also serves a person paid as a professional (e.g. CESU).
  const showPaymentMode = isCompany || form.values.category === 'Professionals & Services';

  const category = <CategorySectionForm form={form} presetKind={isCreation} />;

  return (
    <form className={styles.container} onSubmit={onSubmit}>
      {isCreation && category}
      {isCreation && <ContactKindSectionForm form={form} />}

      {isCompany ? (
        <CompanySectionForm
          form={form}
          header={companyHeader}
          autoFocusName={autoFocusCompanyName}
          onCompanySelected={prefillCompanyAddress}
        />
      ) : (
        <>
          <IdentitySectionForm form={form} />
          {afterIdentity}
        </>
      )}

      {!isCreation && category}
      <ContactSectionForm
        form={form}
        initialAddressText={initialAddressText}
        addressPrefill={addressPrefill}
      />
      {isCompany && <InterlocutorsSectionForm form={form} />}
      {showPaymentMode && <PaymentModeSectionForm form={form} withCustomerReference={isCompany} />}
      <ContactCreationBankSection form={form} />
      {isCompany && <CompanyNotesSectionForm form={form} />}
      <div className={styles.border} />
      <div className={styles.buttons}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('contacts.cancel', 'Annuler')}
        </Button>
        <div className={styles.saveButtons}>
          {submitAndAddAnotherButtonLabel && (
            <Button
              type={'submit'}
              onClick={() => form.setFieldValue('addAnother', true)}
              variant="light"
            >
              {submitAndAddAnotherButtonLabel}
            </Button>
          )}
          <Button variant="primary" type={'submit'} loading={loading}>
            {submitButtonLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default PhysicalContactForm;
